import _ from "lodash";

export const escapeRegexCharacters = (value: any) => {
  if (value && typeof value === "string") {
    return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  }
  return value;
};

export const getNestedValue = (obj: any, field: string) => {
  if (_.get(obj, field) && typeof _.get(obj, field) === "object") {
    return field.split(".").reduce((acc: any, part: any) => acc?.[part], obj).displayText;
  }
  return field.split(".").reduce((acc: any, part: any) => acc?.[part], obj);
};

export const buildPredicate = (q: any): ((item: any) => boolean) => {
  if (!q || !Object.keys(q).length) {
    return () => true;
  }
  if (Object.hasOwn(q, "condition")) {
    const preds = q.rules.map((rule: any) => buildPredicate(rule));
    if (q.condition === "and") {
      return (item: any) => preds.every((p: any) => p(item));
    }
    if (q.condition === "or") {
      return (item: any) => preds.some((p: any) => p(item));
    }
    return () => true;
  }
  const testFunction: any = getFieldOperatorValue({
    operator: q.operator,
    value: q.value,
  });
  return (item: any) => testFunction(getNestedValue(item, q.field));
};

const normalizeToList = (itemValue: any) => {
  if (Array.isArray(itemValue)) return itemValue;
  if (itemValue && typeof itemValue === "object") return [itemValue.displayText];
  return itemValue != null ? [itemValue] : [];
};

export const getFieldOperatorValue = ({ operator, value }: any) => {
  if (Array.isArray(value) && value.length > 0) {
    value = value[0];
  }
  switch (operator) {
    case "contains":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`.*${escapeRegexCharacters(value)}.*`, "i").test(String(val)));
      };
    case "equal":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => val == value);
      };
    case "notequal":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => val !== value);
      };
    case "startswith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`^${escapeRegexCharacters(value)}`, "i").test(val));
      };
    case "endswith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`${escapeRegexCharacters(value)}$`, "i").test(val));
      };
    case "doesnotstartwith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`^${escapeRegexCharacters(value)}`, "i").test(val));
      };
    case "doesnotendwith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`${escapeRegexCharacters(value)}$`, "i").test(val));
      };
    case "doesnotcontain":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`.*${escapeRegexCharacters(value)}.*`, "i").test(val));
      };
    case "isnotempty":
      return (itemValue: any) => itemValue !== undefined && itemValue !== "" && itemValue !== null;
    case "isempty":
      return (itemValue: any) => itemValue === undefined || itemValue === "" || itemValue === null;
    case "greaterthan":
      return (itemValue: any) => {
        return itemValue > value;
      };
    case "greaterthanorequal":
      return (itemValue: any) => {
        return itemValue >= value;
      };
    case "lessthan":
      return (itemValue: any) => {
        return itemValue < value;
      };
    case "lessthanorequal":
      return (itemValue: any) => {
        return itemValue <= value;
      };
  }
};

export const applyFilter = (data: any, tree: any) => {
  if (!tree?.children1 || tree.children1.length === 0) {
    return { filteredData: data, allColumns: new Set() };
  }
  const conjunction = tree.properties?.conjunction || "AND";
  const not = !!tree.properties?.not;
  const allColumns = new Set();
  const filteredData = data.filter((item: any) => {
    const childResults = tree.children1
      .map((child: any) => {
        if (child.type === "group") {
          const groupResult = applyFilter([item], child);
          if (groupResult.allColumns.size === 0) return null;
          groupResult.allColumns.forEach((col: any) => allColumns.add(col));
          return groupResult.filteredData.length > 0;
        }
        let { field, operator, value } = child.properties;
        allColumns.add(field);
        if (!field || !operator) return null;
        const testFunction = getFieldOperatorValue({ operator, value });
        return testFunction ? testFunction(_.get(item, field)) : null;
      })
      .filter((result: any) => result !== null && result !== undefined);
    let result = childResults.every((r: any) => r === true);
    if (conjunction === "OR") {
      result = childResults.includes(true);
    }
    return not ? !result : result;
  });

  return { filteredData, allColumns };
};
